import { useState, useEffect } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { getConfig } from "../../config.js";

interface HealthCheckProps {
  children: React.ReactNode;
}

export const HealthCheck = ({ children }: HealthCheckProps) => {
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true); // Start as true for initial check

  const checkHealth = async () => {
    setIsChecking(true);
    try {
      // Health check endpoint is at the root, not under /api
      const config = getConfig();
      const apiBaseUrl = config.get('api.baseUrl');
      console.log('[HealthCheck] API base URL from config:', apiBaseUrl);

      // Remove /api suffix if present (only from the end of the URL)
      const serverUrl = apiBaseUrl.endsWith('/api')
        ? apiBaseUrl.slice(0, -4)
        : apiBaseUrl;

      const healthUrl = `${serverUrl}/health`;
      console.log('[HealthCheck] Health check URL:', healthUrl);

      const response = await fetch(healthUrl);
      console.log('[HealthCheck] Response status:', response.status);

      if (response.ok) {
        setIsHealthy(true);
      } else {
        setIsHealthy(false);
      }
    } catch (error) {
      console.error("[HealthCheck] Health check failed:", error);
      setIsHealthy(false);
    } finally {
      setIsChecking(false);
    }
  };

  // Run health check on component mount
  useEffect(() => {
    checkHealth();
  }, []);

  // The main app runs entirely on local data — only auth/onboarding flows need the backend
  const BACKEND_PREFIXES = [
    "/register", "/verify-email", "/forgot-password", "/reset-password",
    "/accept-invite", "/onboarding", "/integrations", "/auth",
  ];
  const needsBackend = BACKEND_PREFIXES.some((p) => window.location.pathname.startsWith(p));
  if (!needsBackend) {
    return <>{children}</>;
  }

  // Show children immediately if health check passes
  if (isHealthy === true) {
    return <>{children}</>;
  }

  // Show loading state while checking
  if (isChecking && isHealthy === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Connecting to service...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">
            Service Unavailable
          </h1>
          <p className="text-muted-foreground">
            Unable to connect to the backend service. Please check your
            connection and try again.
          </p>
        </div>
        <button
          onClick={checkHealth}
          disabled={isChecking}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${isChecking ? "animate-spin" : ""}`} />
          {isChecking ? "Connecting..." : "Retry Connection"}
        </button>
      </div>
    </div>
  );
};
